/**
 * Pull the useful message out of a failed SDK call.
 *
 * The generated SDK throws a `ResponseError` whose own `.message` is always the
 * constant "Response returned an error code" — the real text lives in the
 * LoopBack body at `error.message`. Without this, every backend guard
 * (partnership missing, package not reassignable, paid invoice) surfaces as the
 * same generic toast and the actual cause is invisible.
 */

type MaybeResponse = {
  status?: number;
  bodyUsed?: boolean;
  json?: () => Promise<any>;
  data?: any;
};

const getResponse = (error: unknown): MaybeResponse | undefined => {
  const response = (error as { response?: MaybeResponse } | undefined)?.response;
  return response && typeof response === 'object' ? response : undefined;
};

const messageFrom = (body: any): string | undefined => {
  const message = body?.error?.message ?? body?.message;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

export const getApiErrorStatus = (error: unknown): number | undefined => {
  const status = getResponse(error)?.status;
  return typeof status === 'number' ? status : undefined;
};

const readBody = async (response: MaybeResponse): Promise<any | undefined> => {
  if (response.data !== undefined) return response.data;

  // A fetch body can only be read once — reading a consumed stream throws.
  if (response.bodyUsed || typeof response.json !== 'function') return undefined;

  try {
    return await response.json();
  } catch {
    return undefined;
  }
};

export const extractApiErrorMessage = async (
  error: unknown,
  fallback: string,
): Promise<string> => {
  const response = getResponse(error);
  if (!response) return fallback;

  return messageFrom(await readBody(response)) ?? fallback;
};

export interface ApiErrorInfo {
  message: string;
  /**
   * The backend's machine-readable cause. LoopBack's strong-error-handler
   * serializes `code` and `details` for 4xx responses, which is what lets the
   * UI turn a guard into a fix ("no default pricing plan" → offer to set one)
   * instead of an unexplained red toast.
   */
  code?: string;
  details?: any;
  status?: number;
}

export const extractApiError = async (
  error: unknown,
  fallback: string,
): Promise<ApiErrorInfo> => {
  const response = getResponse(error);
  if (!response) {
    return { message: fallback, code: undefined, details: undefined, status: undefined };
  }

  const body = await readBody(response);
  const payload = body?.error ?? body;

  return {
    message: messageFrom(body) ?? fallback,
    code: typeof payload?.code === 'string' ? payload.code : undefined,
    details: payload?.details,
    status: getApiErrorStatus(error),
  };
};
